import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog } from '@mui/material';
import list from 'assets/icons/list.svg';
import { isAxiosError } from 'axios';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Fragment } from 'react/jsx-runtime';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setGroupAdminList } from 'v3/data/store/groupAdmin/actions';
import { clearSellerSelected } from 'v3/data/store/seller/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import * as api from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { InputFormPassword } from 'v3/shared/components/Inputs/InputFormPassword';
import MultipleSelectCheckmarksController from 'v3/shared/components/Inputs/MarkdownSelect';
import { editGroupAdminSchema } from 'v3/shared/schemas/editGroupAdminSchema';
import { createGroupAdminSchema } from 'v3/shared/schemas/groupAdminCreationSchema';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import {
  AlignButtons,
  changeValuePaperPropsStyle,
  Container,
  FiltersContainer,
  Header,
  StyledCloseIcon,
} from './styles';
import { ICreateGroupAdminForm, IGroupAdminModalProps, Mode } from './types';

export const GroupAdminModal: React.FC<IGroupAdminModalProps> = ({
  modal,
  onClose,
  onConfirm,
  fullStoreGrouplist,
  adminStoreGroupList,
}) => {
  const { groupAdmin } = useStoreSelector((store) => store);

  const schema = modal.mode === Mode.Create ? createGroupAdminSchema : editGroupAdminSchema;
  let defaultformValues = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    storeGroupIds: [],
  };

  const {
    control,
    formState: { isValid },
    reset,
    getValues,
  } = useForm<ICreateGroupAdminForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: defaultformValues,
    resolver: yupResolver(schema) as any,
  });

  const handleCreateGroupAdmin = async (formData: ICreateGroupAdminForm) => {
    try {
      await api.createGroupAdmin({
        email: formData.email,
        name: formData.name,
        password: formData.password,
        storeGroupIds: formData.storeGroupIds,
      });
      onClose();
      showSnackbar('Grupo admin criado com sucesso!', 'success', true);
      await getGroupAdminList();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        showSnackbar('E-mail já cadastrado', 'error', true);
        console.error(error);
        return;
      }
      showSnackbar('Erro ao criar grupo admin', 'error', true);
      console.error(error);
    }
  };

  const handleUpdateGroupAdmin = async (formData: ICreateGroupAdminForm) => {
    try {
      await api.updateGroupAdmin({
        name: formData.name,
        storeUserId: groupAdmin.storeUserId,
        storeGroupIds: formData.storeGroupIds,
      });
      onClose();
      showSnackbar('Grupo admin alterado com sucesso!', 'success', true);
      await getGroupAdminList();
    } catch (error) {
      showSnackbar('Erro ao alterar grupo admin', 'error', true);
      console.error(error);
    }
  };

  const onSubmit = async () => {
    const formData = getValues();

    if (modal.mode === Mode.Create) {
      await handleCreateGroupAdmin(formData);
    }

    if (modal.mode === Mode.Edit) {
      await handleUpdateGroupAdmin(formData);
    }
  };

  const getGroupAdminList = async () => {
    try {
      const response = await api.getGroupAdminList(groupAdmin.filter);
      setGroupAdminList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista grupo admins', error);
    }
  };

  useEffect(() => {
    if (modal.mode === Mode.Edit && adminStoreGroupList) {
      reset({
        name: groupAdmin.name,
        email: '',
        password: '',
        confirmPassword: '',
        storeGroupIds: adminStoreGroupList.map((item) => item!.storeGroup.id),
      });
      return;
    }

    reset(defaultformValues);
  }, [modal.active, modal.mode, adminStoreGroupList, reset]);

  return (
    <Dialog
      onClose={() => {
        onClose();
        reset();
        clearSellerSelected();
      }}
      open={modal.active}
      aria-labelledby="customized-dialog-title"
      PaperProps={changeValuePaperPropsStyle}
    >
      <Container>
        <FiltersContainer>
          <Header>
            <img src={list} alt="list" />
            <SubtitleLarge>{modal.title}</SubtitleLarge>
            <StyledCloseIcon
              onClick={() => {
                onClose();
                reset();
              }}
            />
          </Header>
          <InputFormController
            data-testid="input-group-admin-name"
            formControl={control}
            formControlName="name"
            label="Nome do administrador"
            placeholder="Nome do administrador"
          />
          {modal.mode === Mode.Create && (
            <Fragment>
              <InputFormController
                formControl={control}
                data-testid="input-group-admin-email"
                inputType="email"
                formControlName="email"
                label="E-mail"
                placeholder="E-mail"
              />

              <InputFormPassword
                data-testid="input-group-admin-password"
                formControl={control}
                formControlName="password"
                label="Senha"
              />

              <InputFormPassword
                data-testid="input-group-admin-confirmPassword"
                formControl={control}
                formControlName="confirmPassword"
                label="Confirmar senha"
              />
            </Fragment>
          )}

          <MultipleSelectCheckmarksController
            name="storeGroupIds"
            control={control}
            options={fullStoreGrouplist}
            label="Grupos"
            fullWidth
          />
        </FiltersContainer>

        <AlignButtons>
          <FlexButton text="Salvar" size="small" disabled={!isValid} handleClick={onSubmit} />
          <TextButton
            text="Cancelar"
            $outline
            size="small"
            handleClick={() => {
              reset();
              onClose();
            }}
          />
        </AlignButtons>
      </Container>
    </Dialog>
  );
};
